import { useEffect, useRef, useState } from 'react';
import { Chip, Input, Modal, ModalBody, ModalContent, ModalHeader, Spinner } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import { agentBuilderApi, BuilderApiError, type InboxEmail, type RunState, type ToolInfo } from './agentBuilderApi';

const STEP_ICON: Record<string, { icon: string; className: string }> = {
  running: { icon: 'lucide:loader-circle', className: 'animate-spin text-primary' },
  done: { icon: 'lucide:circle-check', className: 'text-success' },
  failed: { icon: 'lucide:circle-x', className: 'text-danger' },
};

// A live test run of the published journey on the deployed agent platform: real mailbox, advisor
// and Jira. Replies reach a customer only if the platform's send allowlist lets them through.
export function RunDialog({
  name,
  isOpen,
  onOpenChange,
  tools,
  behind,
}: {
  name: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  tools: ToolInfo[];
  behind: boolean; // the workflow has changes that are not published: the run uses the published one
}) {
  const { t } = useTranslation();
  const [needed, setNeeded] = useState<string[] | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [inbox, setInbox] = useState<InboxEmail[] | null>(null);
  const [subject, setSubject] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [run, setRun] = useState<RunState | null>(null);
  const [running, setRunning] = useState(false);
  // The run starts by itself on the newest email the journey would pick up; the list of the
  // journey's other emails shows only when the user asks to use a different one.
  const [choosing, setChoosing] = useState(false);
  const autoStarted = useRef(false);
  const abort = useRef<AbortController | null>(null);

  // The journey's own emails (its subject filter), newest first; the newest is selected, as it is
  // the one the journey would pick up now. Another can still be picked.
  const loadInbox = () => {
    setInbox(null);
    agentBuilderApi.testEmails(name).then(
      (res) => {
        setSubject(res.subject_contains);
        setInbox(res.emails);
        setValues((v) => ({ ...v, message_id: res.emails[0]?.message_id ?? '' }));
      },
      (e: Error) => setLoadError(e.message),
    );
  };

  useEffect(() => {
    if (!isOpen) return;
    setLoadError(null);
    setRun(null);
    setChoosing(false);
    autoStarted.current = false;
    agentBuilderApi.inputs(name).then(
      (list) => {
        setNeeded(list);
        setValues((v) => Object.fromEntries(list.map((k) => [k, v[k] ?? ''])));
        if (list.includes('message_id')) loadInbox();
      },
      (e: BuilderApiError) => setLoadError(e.status === 404 || e.status === 409 ? t('Publish this journey first, then run it.') : e.message),
    );
    return () => abort.current?.abort(); // closing the dialog stops following the run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, name]);

  const start = async () => {
    setChoosing(false);
    abort.current = new AbortController();
    setRunning(true);
    setRun({ status: 'running', steps: [] });
    try {
      await agentBuilderApi.run(name, values, setRun, abort.current.signal);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setRun((r) => ({ ...r, status: 'failed', error: (e as Error).message }));
    } finally {
      setRunning(false);
    }
  };

  const toolName = (tool: string) => tools.find((x) => x.name === tool)?.display_name || tool;
  const outcome = run?.outcome as { route?: string; ticket_id?: string | null; reply_sent?: boolean; reasoning?: string } | null | undefined;
  const ready = !!needed && needed.every((k) => values[k]);
  const newest = inbox?.find((m) => m.message_id === values.message_id);

  // Nothing to ask (the email is the only input and the journey's newest one is known): run now.
  useEffect(() => {
    if (!isOpen || autoStarted.current || running || choosing || !ready) return;
    if (!needed?.includes('message_id') || needed.some((k) => k !== 'message_id')) return;
    autoStarted.current = true;
    void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ready, needed, running, choosing]);

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="4xl" scrollBehavior="inside" isDismissable={!running}>
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          {t('Test run')}
          <span className="text-xs font-normal text-default-500">
            {t('Runs the published journey live: it reads the real mailbox, asks the advisor and can create Jira tickets. Replies are sent only to allowed addresses.')}
          </span>
        </ModalHeader>
        <ModalBody className="pb-6" data-testid="ab-run-dialog">
          {behind && <p className="rounded bg-warning/10 px-3 py-2 text-sm text-warning-700">{t('This journey has unpublished changes. The run uses the published version.')}</p>}
          {loadError && <p className="text-sm text-danger">{loadError}</p>}

          {needed?.includes('message_id') && !choosing && (
            <section className="flex flex-wrap items-center gap-2 rounded border border-default-200 px-3 py-2 text-sm" data-testid="ab-run-email">
              {!inbox && !loadError && <Spinner size="sm" />}
              {inbox?.length === 0 && (
                <span className="text-default-500">
                  {subject ? t('No emails with “{{subject}}” in the subject among the newest in the mailbox.', { subject }) : t('No emails in the mailbox.')}
                </span>
              )}
              {newest && (
                <span className="min-w-0 flex-1">
                  <span className="block text-xs text-default-500">
                    {subject ? t('The newest email with “{{subject}}” in the subject', { subject }) : t('The newest email in the support mailbox')}
                  </span>
                  <span className="block truncate font-medium">{newest.subject || t('(no subject)')}</span>
                  <span className="block truncate text-xs text-default-500">
                    {newest.from} · {new Date(newest.received_at).toLocaleString()}
                  </span>
                </span>
              )}
              {!!inbox?.length && (
                <CtyButton size="sm" variant="light" color="primary" onPress={() => setChoosing(true)} isDisabled={running} id="ab-run-choose">
                  {t('Use a different email')}
                </CtyButton>
              )}
            </section>
          )}

          {needed?.includes('message_id') && choosing && (
            <section>
              <div className="mb-1 flex items-center justify-between">
                <h3 className="text-sm font-semibold">
                  {subject
                    ? t('Emails with “{{subject}}” in the subject: the newest is selected', { subject })
                    : t('Newest emails in the support mailbox: the newest is selected')}
                </h3>
                <CtyButton size="sm" variant="light" onPress={loadInbox} isDisabled={running} title={t('Refresh')}>
                  <Icon icon="lucide:refresh-cw" width={14} />
                </CtyButton>
              </div>
              {!inbox && !loadError && <Spinner size="sm" />}
              {inbox?.length === 0 && (
                <p className="text-sm text-default-500">
                  {subject ? t('No emails with “{{subject}}” in the subject among the newest in the mailbox.', { subject }) : t('No emails in the mailbox.')}
                </p>
              )}
              <ul className="max-h-56 overflow-y-auto rounded border border-default-200" data-testid="ab-run-inbox">
                {inbox?.map((m) => {
                  const picked = values.message_id === m.message_id;
                  return (
                    <li key={m.message_id}>
                      <button
                        type="button"
                        disabled={running}
                        onClick={() => setValues({ ...values, message_id: m.message_id })}
                        className={`flex w-full items-center gap-2 border-b border-default-100 px-3 py-2 text-left text-sm ${picked ? 'bg-primary/15' : 'hover:bg-default-100'}`}
                        aria-pressed={picked}
                      >
                        <Icon icon={picked ? 'lucide:circle-dot' : 'lucide:circle'} width={14} className="shrink-0 text-primary" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{m.subject || t('(no subject)')}</span>
                          <span className="block truncate text-xs text-default-500">
                            {m.from} · {new Date(m.received_at).toLocaleString()}
                          </span>
                        </span>
                        {m.processed && (
                          <Chip size="sm" variant="flat">
                            {t('Processed before')}
                          </Chip>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
          {needed
            ?.filter((k) => k !== 'message_id')
            .map((k) => (
              <Input key={k} size="sm" label={k} value={values[k] ?? ''} isDisabled={running} onValueChange={(v) => setValues({ ...values, [k]: v })} />
            ))}

          <div className="flex justify-end">
            <CtyButton color="primary" onPress={start} isDisabled={!ready || running} isLoading={running} id="ab-run-start">
              {running ? t('Running…') : run ? t('Run again') : t('Run')}
            </CtyButton>
          </div>

          {run && (
            <section data-testid="ab-run-result">
              <h3 className="mb-1 text-sm font-semibold">{t('Steps')}</h3>
              <ol className="flex flex-col gap-1">
                {(run.steps ?? []).map((s) => {
                  const look = STEP_ICON[s.status] ?? STEP_ICON.running;
                  return (
                    <li key={s.id} className="flex items-start gap-2 rounded border border-default-100 px-2 py-1.5 text-sm" data-testid={`ab-run-step-${s.tool}`}>
                      <Icon icon={look.icon} width={16} className={`mt-0.5 shrink-0 ${look.className}`} />
                      <span className="min-w-0">
                        <span className="font-medium">{toolName(s.tool)}</span> <span className="text-xs text-default-400">{s.tool}</span>
                        {s.summary && <span className="block break-words text-xs text-default-600">{s.summary}</span>}
                      </span>
                    </li>
                  );
                })}
                {running && !run.steps?.length && <li className="text-sm text-default-500">{t('Starting…')}</li>}
              </ol>
              {outcome && (
                <div className="mt-3 rounded border border-default-200 p-3 text-sm" data-testid="ab-run-outcome">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <Chip size="sm" color="primary" variant="flat">
                      {outcome.route ?? t('done')}
                    </Chip>
                    {outcome.ticket_id && <Chip size="sm" variant="flat">{t('Ticket {{id}}', { id: outcome.ticket_id })}</Chip>}
                    {outcome.reply_sent !== undefined && (
                      <Chip size="sm" variant="flat" color={outcome.reply_sent ? 'success' : 'default'}>
                        {outcome.reply_sent ? t('Reply sent') : t('No reply sent')}
                      </Chip>
                    )}
                  </div>
                  {outcome.reasoning && <p className="whitespace-pre-wrap text-default-700">{outcome.reasoning}</p>}
                </div>
              )}
              {run.error && <p className="mt-2 text-sm text-danger">{run.error}</p>}
              {!outcome && !run.error && !running && run.message && <p className="mt-2 text-sm text-default-600">{run.message}</p>}
            </section>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
